/* eslint-disable import/order */
import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Loader2,
  AlertCircle,
  Trash2,
} from "lucide-react";

import Checkbox from "../Checkbox/Checkbox";
import TeamBadge from "../TeamBadge/TeamBadge";
import dashboardBack from "../../assets/images/dashboard-back.svg";
/* eslint-enable import/order */

const ROWS_OPTIONS = [5, 10, 25, 50];


/**
 * Shared PlayersTab Component
 * Used by both TeamDashboard (public, editable) and TeamOverview (admin, read-only)
 *
 * Props:
 *   players           - full player array (filtering + pagination handled internally)
 *   topPlayers        - top players array [{ name, stat, value }]
 *   loading           - show loading spinner
 *   error             - show error message
 *   searchQuery       - controlled search value
 *   setSearchQuery    - setter for search
 *   isReadOnly        - hides add/delete controls (default: false)
 *   selectedPlayers   - array of selected player IDs (edit mode)
 *   setSelectedPlayers- setter for selected players (edit mode)
 *   onAddClick        - callback to open add modal (edit mode)
 *   onDeleteClick     - callback to delete a player (edit mode)
 *   onBulkDeleteClick - callback for bulk delete (edit mode)
 */
const PlayersTab = ({
  players = [],
  topPlayers = [],
  loading = false,
  error = null,
  searchQuery = "",
  setSearchQuery,
  isReadOnly = false,
  selectedPlayers = [],
  setSelectedPlayers,
  onAddClick,
  onDeleteClick,
  onBulkDeleteClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const rowsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rowsDropdownRef.current && !rowsDropdownRef.current.contains(e.target))
        setShowRowsDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter + paginate locally
  const filtered = players.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPlayers = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalPlayers / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalPlayers);
  const paginated = filtered.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);
  useEffect(() => { setCurrentPage(1); }, [rowsPerPage]);

  const handleSelectAll = (e) => {
    if (!setSelectedPlayers) return;
    setSelectedPlayers(e.target.checked ? paginated.map((p) => p._id) : []);
  };

  const handleSelectPlayer = (id) => {
    if (!setSelectedPlayers) return;
    setSelectedPlayers(
      selectedPlayers.includes(id)
        ? selectedPlayers.filter((p) => p !== id)
        : [...selectedPlayers, id]
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-[#00E7FF] animate-spin mb-4" />
        <p className="text-app-text-secondary">Loading players...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-[#FF7B7B] mb-4" />
        <p className="text-[#FF7B7B] mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-app-card text-app-text rounded-full hover:bg-app-card/80 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${isReadOnly ? "pt-12" : "pt-20"}`}>
        <img src={dashboardBack} alt="" className="w-100 h-80 object-contain mb-8" />
        <h2 className="text-[16px] font-semibold text-app-text mb-2">
          There is no information about players yet
        </h2>
        <p className="text-app-text-secondary text-center text-[12px] max-w-md mb-6">
          {isReadOnly
            ? "No players have been added to this team yet."
            : "To start tracking players, add them to the roster list."}
        </p>
        {!isReadOnly && (
          <button
            onClick={onAddClick}
            className="px-[120px] py-3 bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium rounded-full transition-all active:scale-95"
          >
            Add player
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-app-text mb-3">Players</h2>

      {/* Top Players */}
      {topPlayers.length > 0 && (
        <div className="border border-app-border rounded-2xl mb-6 overflow-hidden">
          <div className="bg-app-surface border-b border-app-border px-4 flex items-center py-4">
            <p className="text-xs text-app-text-secondary">Top players</p>
          </div>
          <div className="grid grid-cols-2 gap-4 px-5 py-4">
            {topPlayers.map((player, idx) => (
              <div key={idx} className="flex items-center justify-between mr-2">
                <div className="flex items-center">
                  <TeamBadge
                    src={player.avatar}
                    alt={player.name}
                    containerSize="w-10 h-10"
                    size="w-4 h-4"
                  />
                  <div className="flex flex-col items-start justify-center ml-2">
                    <p className="text-sm text-app-text font-medium">{player.name}</p>
                    <p className="text-xs text-app-text-secondary">{player.stat}</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-app-text">{player.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Actions */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-app-text-secondary">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Search player"
            value={searchQuery}
            onChange={(e) => setSearchQuery?.(e.target.value)}
            className="w-full bg-blue-shade-8 border border-blue-shade-8 rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
          />
        </div>
        {!isReadOnly && selectedPlayers.length > 0 && (
          <button
            onClick={onBulkDeleteClick}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF7B7B] text-white rounded-full font-medium text-sm"
          >
            <Trash2 className="w-4 h-4" />
            Delete ({selectedPlayers.length})
          </button>
        )}
        {!isReadOnly && (
          <button
            onClick={onAddClick}
            className="flex items-center gap-1 px-4 py-2 bg-[#082441] hover:opacity-50 text-app-text text-[15px] font-medium rounded-full transition-all active:scale-95 ml-auto"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        )}
      </div>

      {/* Players Table */}
      <div className="w-full overflow-x-auto rounded-2xl border border-app-hover">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-app-surface border-b border-app-hover">
              {!isReadOnly && (
                <th className="p-4 w-12">
                  <Checkbox
                    checked={selectedPlayers.length === paginated.length && paginated.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th className="p-4 text-sm font-medium text-app-text-secondary">Member name</th>
              <th className="p-4 text-sm font-medium text-app-text-secondary">Email</th>
              {!isReadOnly && <th className="p-4 w-12" />}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 && (
              <tr>
                <td colSpan={isReadOnly ? 2 : 4} className="py-10 text-center text-sm text-app-text-secondary">
                  No players match your search.
                </td>
              </tr>
            )}
            {paginated.map((player) => (
              <tr
                key={player._id}
                className={`border-b border-app-hover transition-colors group ${
                  selectedPlayers.includes(player._id) ? "bg-app-hover" : ""
                }`}
              >
                {!isReadOnly && (
                  <td className="px-4">
                    <Checkbox
                      checked={selectedPlayers.includes(player._id)}
                      onChange={() => handleSelectPlayer(player._id)}
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <PlayerAvatar name={player.name} />
                    <span className="text-sm font-medium text-app-text">{player.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-app-text">{player.email || "—"}</td>
                {!isReadOnly && (
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        onClick={() => onDeleteClick?.(player)}
                        className="text-app-text-secondary hover:text-[#FF7B7B] hover:bg-app-hover p-1.5 rounded-2xl transition-colors"
                        title="Delete player"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPlayers > 0 && (
        <div className="flex items-center justify-between py-3">
          {/* Left: Prev / Next */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-app-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={18} className="text-app-text" />
            </button>
            <button
              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-app-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={18} className="text-app-text" />
            </button>
          </div>

          {/* Center: count */}
          <span className="text-sm text-app-text-muted">
            Show {startIndex + 1}–{endIndex} of {totalPlayers} {totalPlayers === 1 ? "member" : "members"}
          </span>

          {/* Right: Rows per page */}
          <div className="flex items-center gap-2 text-sm text-app-text-muted">
            <span>Rows per page</span>
            <div className="relative" ref={rowsDropdownRef}>
              <button
                onClick={() => setShowRowsDropdown(!showRowsDropdown)}
                className="flex items-center gap-1 bg-app-surface px-3 py-1.5 rounded-2xl text-app-text font-medium"
              >
                {rowsPerPage} Row
                <ChevronUp
                  size={16}
                  className={`transition-transform duration-200 shrink-0 ${showRowsDropdown ? "rotate-180" : ""}`}
                />
              </button>
              {showRowsDropdown && (
                <div className="absolute right-0 bottom-full mb-2 w-24 bg-[#000F1F] border border-app-hover rounded-xl shadow-xl z-50 overflow-hidden">
                  {ROWS_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setRowsPerPage(option); setShowRowsDropdown(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors border-b border-app-hover last:border-b-0 ${
                        rowsPerPage === option ? "text-app-accent" : "text-app-text hover:bg-app-hover"
                      }`}
                    >
                      {option} Row
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PlayerAvatar = ({ name }) => {
  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";
  return (
    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center flex-shrink-0">
      <span className="text-white text-[9px] font-medium">{initials}</span>
    </div>
  );
};

export default PlayersTab;
